import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  deleteSchedule,
  fetchScheduleFires,
  fetchSchedules,
  scheduleFiresQueryKey,
  schedulesQueryKey,
  updateSchedule,
} from '../lib/api';
import type { ScheduleFire, ScheduledRequest, SpaceState } from '../lib/api';
import { scheduleName, scheduleRowSentence, soonestArmed } from '../lib/schedule';
import {
  fireIsAmber,
  fireTickLabel,
  isAmber,
  scheduleMetaAffix,
  scheduleState,
  scheduleStatePhrase,
} from '../lib/scheduleAliveness';

// ─────────────────────────────────────────────────────────────────────────────
// SpaceSchedules — the quiet rows the composer's ▾ creates (T-0156), now speaking
// the honest states (T-0157).
//
// They live beside the space's Check-in line because they answer the same question:
// *when does this space do something on its own?* Check-in is a sleep timeout; a
// Schedule is a Request the server presses Send on. One meta-line, two cadences —
// no new tab, no new surface, no ambient chrome (the chrome budget).
//
// A space with no Schedules renders NOTHING. The affordance to make one lives on the
// composer, where the Request is; an empty-state here would be a second front door
// to a feature that deliberately has one.
//
// Every row is a ● platform Schedule — a row in `scheduled_requests`, swept by the
// server — so it is allowed the future tense, and it is the ONLY surface here that
// is. The ● is left unmarked precisely because it is the default register; ◐ is the
// one that must announce itself (see `lib/scheduleAliveness.ts`).
// ─────────────────────────────────────────────────────────────────────────────

/** How many ticks the history strip keeps. A week of mornings. */
const HISTORY_TICKS = 7;

export function SpaceSchedules({
  spaceId,
  spaceState,
}: {
  spaceId: string;
  /** `sleeping` lets a row rest honestly ("asleep · wakes 8:00") instead of
   *  claiming a pulse it does not have. */
  spaceState?: SpaceState;
}) {
  // Fetched on mount — the space sheet only mounts when the drawer opens, so this
  // IS "reads GET on sheet open".
  const { data, isError } = useQuery({
    queryKey: schedulesQueryKey(spaceId),
    queryFn: () => fetchSchedules(spaceId),
  });

  const schedules = data?.schedules ?? [];

  if (isError) {
    return (
      <p className="spaces-row-schedules-error" role="status">
        Couldn't load this space's Schedules.
      </p>
    );
  }

  if (schedules.length === 0) return null;

  return (
    <ul className="spaces-row-schedules" aria-label="Scheduled Requests">
      {schedules.map((schedule) => (
        <ScheduleRow
          key={schedule.schedule_id}
          spaceId={spaceId}
          schedule={schedule}
          spaceState={spaceState}
        />
      ))}
    </ul>
  );
}

/**
 * The other half of the cadence story on the space-sheet meta-line:
 * "Check-in every 4h · **↻ Daily 8:00**".
 *
 * A cadence, read off the rule — not a next run, and so not a tense. That is why
 * it needs no fire history to stay honest, and why it can sit one line above a row
 * confessing "Didn't run at 8:00 — ran 8:13 ✓" without contradicting it: it says
 * what the Schedule *is*, exactly as "Check-in every 4h" beside it does.
 *
 * The soonest armed Schedule speaks for the space; a silenced one has no cadence
 * to contribute, and a space with none contributes no separator either.
 */
export function ScheduleMetaAffix({ spaceId }: { spaceId: string }) {
  const { data } = useQuery({
    queryKey: schedulesQueryKey(spaceId),
    queryFn: () => fetchSchedules(spaceId),
  });

  const rule = soonestArmed(data?.schedules)?.repeat_rule;
  const sentence = rule ? scheduleMetaAffix(rule) : null;
  if (!sentence) return null;

  return (
    <>
      <span className="spaces-row-meta-sep" aria-hidden="true">
        ·
      </span>
      <span className="spaces-row-meta-schedule">{sentence}</span>
    </>
  );
}

function ScheduleRow({
  spaceId,
  schedule,
  spaceState,
}: {
  spaceId: string;
  schedule: ScheduledRequest;
  spaceState?: SpaceState;
}) {
  const queryClient = useQueryClient();
  const [confirmRemove, setConfirmRemove] = useState(false);
  const name = scheduleName(schedule.request_body);

  // What this Schedule actually DID. A Schedule that has never run cannot have
  // missed anything, and its history is empty by construction — `last_fired_at`
  // is the Schedule's own memory of that, so we don't ask. A backend without the
  // route answers the same way: no fires, no confession, which is the correct
  // silence rather than a guess.
  const { data: fireData } = useQuery({
    queryKey: scheduleFiresQueryKey(spaceId, schedule.schedule_id),
    queryFn: () => fetchScheduleFires(spaceId, schedule.schedule_id),
    enabled: schedule.last_fired_at !== null,
  });
  const fires = fireData?.fires ?? [];

  const state = scheduleState({
    enabled: schedule.enabled,
    nextFireAt: schedule.next_fire_at,
    spaceAsleep: spaceState === 'sleeping',
    latestFire: fires[0],
  });
  const phrase = scheduleStatePhrase(state);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: schedulesQueryKey(spaceId) });
    queryClient.invalidateQueries({
      queryKey: scheduleFiresQueryKey(spaceId, schedule.schedule_id),
    });
  };

  const toggleMutation = useMutation({
    mutationFn: (enabled: boolean) =>
      updateSchedule(spaceId, schedule.schedule_id, { enabled }),
    // The server recomputes `next_fire_at` from now on an off→on flip (silence is a
    // pause, never a queue), so the row's next-run time comes from the refetch, never
    // from an optimistic guess here.
    onSuccess: invalidate,
  });

  const removeMutation = useMutation({
    mutationFn: () => deleteSchedule(spaceId, schedule.schedule_id),
    onSuccess: () => {
      setConfirmRemove(false);
      invalidate();
    },
  });

  const busy = toggleMutation.isPending || removeMutation.isPending;

  return (
    <li className="spaces-row-schedule">
      {/* The name ellipsizes when the sheet is narrow; the state never does — it
          is the whole reason the row exists. The full sentence stays in the title. */}
      <span
        className="spaces-row-schedule-text"
        title={scheduleRowSentence(schedule.request_body, phrase)}
      >
        <span className="spaces-row-schedule-glyph" aria-hidden="true">
          ↻
        </span>
        <span className="spaces-row-schedule-name">{name}</span>
        <span
          className="spaces-row-schedule-next"
          data-tone={isAmber(state) ? 'amber' : undefined}
        >
          · {phrase}
        </span>
      </span>

      <button
        type="button"
        role="switch"
        aria-checked={schedule.enabled}
        aria-label={name}
        className="spaces-row-schedule-toggle"
        disabled={busy}
        onClick={() => toggleMutation.mutate(!schedule.enabled)}
      >
        <span aria-hidden="true">{schedule.enabled ? 'On' : 'Off'}</span>
      </button>

      {!confirmRemove ? (
        <button
          type="button"
          className="spaces-row-schedule-remove"
          disabled={busy}
          onClick={() => setConfirmRemove(true)}
          aria-label={`Remove Scheduled Request: ${name}`}
        >
          Remove
        </button>
      ) : (
        <span
          className="spaces-row-schedule-confirm"
          role="group"
          aria-label={`Confirm remove ${name}`}
        >
          <button
            type="button"
            className="spaces-row-schedule-remove"
            disabled={busy}
            onClick={() => removeMutation.mutate()}
          >
            {removeMutation.isPending ? 'Removing…' : 'Confirm'}
          </button>
          <button
            type="button"
            className="spaces-row-schedule-cancel"
            disabled={busy}
            onClick={() => setConfirmRemove(false)}
          >
            Cancel
          </button>
        </span>
      )}

      <FireHistory fires={fires} />
    </li>
  );
}

/**
 * One tick per recent fire, oldest → newest. **History keeps its amber tick** —
 * a run that was late, never landed, or was never answered stays marked, and
 * scrolling past it never launders it into a green day-dot.
 */
function FireHistory({ fires }: { fires: readonly ScheduleFire[] }) {
  if (fires.length === 0) return null;
  const recent = fires.slice(0, HISTORY_TICKS).reverse();

  return (
    <ul className="spaces-row-schedule-fires" aria-label="Recent runs, oldest first">
      {recent.map((fire) => {
        const label = fireTickLabel(fire);
        return (
          <li
            key={fire.fire_id}
            className="spaces-row-schedule-fire"
            data-tone={fireIsAmber(fire) ? 'amber' : undefined}
            title={label}
          >
            <span className="visually-hidden">{label}</span>
          </li>
        );
      })}
    </ul>
  );
}
